import React, { ReactElement, useContext, useRef } from 'react';
import { DraggableContext } from '.';

export interface IDraggableItem {
    children?: React.ReactNode;
    isDrop?: boolean;
}

export const DraggableItem: React.FC<IDraggableItem> = ({ children, isDrop }) => {
    const ref = useRef<HTMLElement>(null);
    const manager = useContext(DraggableContext);

    type DraggableChildProps = React.HTMLAttributes<HTMLElement> & {
        ref?: React.Ref<HTMLElement>;
        style?: React.CSSProperties;
    };

    const onMouseDown = (e: React.MouseEvent<HTMLElement>) => {
        if (manager.wasDragging) {
            return;
        }
        manager.onMouseDown(e.nativeEvent);
    };

    const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
        manager.setItemHtmlElement(ref.current as HTMLElement);
    };

    const onClick = (e: React.MouseEvent<HTMLElement>) => {
        if (!manager.wasDragging) {
            const clickableElement = (children as ReactElement)?.props as {
                onClick?: (e: React.MouseEvent<HTMLElement>) => void;
            };
            clickableElement?.onClick?.(e);
        } else {
            manager.wasDragging = false;
        }
    };

    const onTouchStart = (e: React.TouchEvent<HTMLElement>) => {
        manager.wasDragging = false;
    };

    const child = React.Children.only(children) as ReactElement<DraggableChildProps>;
    const setNodeRef = (node: HTMLElement | null) => {
        ref.current = node;

        const childRef = child.props.ref;
        if (!childRef) {
            return;
        }

        if (typeof childRef === 'function') {
            childRef(node);
        } else {
            (childRef as React.RefObject<HTMLElement | null>).current = node;
        }
    };

    return React.cloneElement(child, {
        ref: setNodeRef,
        onClick,
        onMouseDown,
        onPointerDown,
        onTouchStart,
        style: {
            ...child.props.style,
            userSelect: 'none',
            WebkitTouchCallout: 'none'
        },
        role: isDrop ? 'drop' : undefined
    });
};
